import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../../context/AppContext";
import { createTimeLog } from "../../api/timeLog";

const AddTimeLogDialog = ({ open, onClose, task_id, onSuccess }) => {
    const { t } = useTranslation();
    const { setIsLoading } = useAppContext();
    const [error, setError] = useState(null);
    const [form, setForm] = useState({
        task_id: task_id || "",
        logged_date: "",
        logged_minutes: "",
    });

    // Time options with corresponding minutes in number format
    const timeOptions = [
        { label: "0:30", value: 30 },
        { label: "1:00", value: 60 },
        { label: "1:30", value: 90 },
        { label: "2:00", value: 120 },
        { label: "2:30", value: 150 },
        { label: "3:00", value: 180 },
        { label: "3:30", value: 210 },
        { label: "4:00", value: 240 },
        { label: "4:30", value: 270 },
        { label: "5:00", value: 300 },
        { label: "5:30", value: 330 },
        { label: "6:00", value: 360 },
        { label: "6:30", value: 390 },
        { label: "7:00", value: 420 },
        { label: "7:30", value: 450 },
        { label: "8:00", value: 480 },
        { label: "8:30", value: 510 },
        { label: "9:00", value: 540 }
    ];

    useEffect(() => {
        if (task_id) {
            setForm({
                ...form,
                task_id: task_id, // Set the task_id passed from props
            });
        }
    }, [task_id]);

    const handleSubmit = async () => {
        if (!form.logged_date || !form.logged_minutes) {
            setError(t("fields.all_required_error"));
            return;
        }

        setIsLoading(true);
        try {
            await createTimeLog(form); // Assuming createTimeLog makes a POST request to add the time log
            setError(null);
            onSuccess?.();
            setForm({
                task_id: "",
                logged_date: "",
                logged_minutes: "",
            });
            onClose();
        } catch (err) {
            console.error("Save error", err);
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ color: "#000" }}>
                {t("common.log_time")}
            </DialogTitle>
            <DialogContent sx={{ paddingTop: "5px !important" }}>
                {error && (
                    <Box mb={2}>
                        <Box
                            sx={{
                                bgcolor: "#fdecea",
                                color: "#b71c1c",
                                px: 2,
                                py: 1.5,
                                borderRadius: 1,
                                fontSize: 14,
                                fontWeight: 500,
                            }}
                        >
                            {t(`${error}`) || error}
                        </Box>
                    </Box>
                )}

                {/* Time Log Form Fields */}
                <Box sx={{ marginBottom: "16px" }}>
                    <TextField
                        fullWidth
                        label={t("fields.logged_date")}
                        type="date"
                        value={form.logged_date}
                        onChange={(e) => setForm({ ...form, logged_date: e.target.value })}
                        required
                        InputLabelProps={{
                            shrink: true,
                        }}
                        sx={{
                            '& .MuiInputBase-root': {
                                backgroundColor: "transparent",
                            },
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: "#999",
                                },
                                '&:hover fieldset': {
                                    borderColor: "#666",
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: "#333",
                                },
                            },
                            '& .MuiInputBase-input': {
                                color: "#000",
                            },
                            '& .MuiInputLabel-root': {
                                color: "#666",
                                "&.Mui-focused": {
                                    color: "#000",
                                },
                            },
                        }}
                    />
                </Box>

                {/* Time Duration (logged_minutes) Field */}
                <Box sx={{ marginBottom: "16px" }}>
                    <TextField
                        fullWidth
                        select
                        label={t("fields.logged_time")}
                        value={form.logged_minutes}
                        onChange={(e) => setForm({ ...form, logged_minutes: e.target.value })}
                        required
                        SelectProps={{
                            native: true,
                        }}
                        sx={{
                            '& .MuiInputBase-root': {
                                backgroundColor: "transparent",
                            },
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: "#999",
                                },
                                '&:hover fieldset': {
                                    borderColor: "#666",
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: "#333",
                                },
                            },
                            '& .MuiInputBase-input': {
                                color: "#000",
                            },
                            '& .MuiInputLabel-root': {
                                color: "#666",
                                "&.Mui-focused": {
                                    color: "#000",
                                },
                            },
                        }}
                    >
                        {timeOptions.map((option, index) => (
                            <option key={index} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </TextField>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} sx={{ color: "#000" }}>
                    {t("actions.cancel")}
                </Button>
                <Button onClick={handleSubmit} sx={{ backgroundColor: "#004d40", color: "white", padding: '10px' }}>
                    {t("actions.save")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddTimeLogDialog;
